import * as THREE from 'three'
import type { GroundPlane } from '../depth/ground'
import { fitSupport } from '../depth/surface-probe'
import type { SkinMode } from './config'
import type { Occluder } from './occluder'

/**
 * Colours every sensed cell by what the solver would make of it: ground, relief
 * small enough to be discarded as phantom, or a wall the ring probe would push
 * a box away from.
 *
 * This is the only way to see how much of a real object actually became
 * collidable, and in what shape. It is also the only honest readout of "how
 * much is being detected", because the boxes only tell you about the cells they
 * happen to be standing next to.
 *
 * An earlier version fitted its own plane over a 0.4 m disc with no outlier
 * rejection while the floor used a different plane over 1.5 m with three
 * refinement passes. Perfectly locked cells then painted amber wherever the two
 * disagreed on tilt, which is a broad smooth gradient across the whole floor
 * rather than bumps — and every visual judgement made through it was worthless.
 * It uses the floor's own plane whenever there is one.
 */

/** Sunk relative to ground — a hole, a drop-off, or a depth artefact. */
const BELOW: readonly number[] = [0.1, 0.11, 0.14]
/** Ground. Nothing here would stop a box. */
const GROUND: readonly number[] = [0.16, 0.32, 0.4]
/** Sensed relief the solver deliberately throws away as phantom. */
const IGNORED: readonly number[] = [0.3, 0.52, 0.34]
/** Collidable, at the climb threshold. */
const WALL_LOW: readonly number[] = [1.0, 0.72, 0.2]
/** Collidable, this far above the threshold or higher. */
const WALL_HIGH: readonly number[] = [1.0, 0.24, 0.24]

const WALL_SPAN = 0.04
const BELOW_METRES = -0.02
const GROUND_BAND_METRES = 0.002

/** Fallback fit radius, used only when no floor plane exists. */
const REFERENCE_RADIUS = 0.4

export const SKIN_LEGEND =
  'Skin: blue = ground · green = relief discarded as phantom · amber→red = collidable, ' +
  'redder is taller · black = no depth.'

export class Skin {
  private readonly mesh: THREE.Mesh
  private readonly solid: THREE.MeshStandardMaterial
  private readonly wire: THREE.MeshBasicMaterial
  private mode: SkinMode = 'off'

  constructor(scene: THREE.Scene, occluder: Occluder) {
    // Same lit, faceted material as the boxes so the sensed surface reads as a
    // solid object rather than an overlay. `flatShading` derives its normal
    // from screen-space derivatives, so the geometry needs no normal attribute.
    this.solid = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.5,
      metalness: 0.05,
      flatShading: true,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide,
      depthWrite: false,
    })
    // Wireframe lines have no meaningful facet, so they stay unlit.
    this.wire = new THREE.MeshBasicMaterial({
      vertexColors: true,
      wireframe: true,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
    })

    // Shares the occluder's geometry. Separate mesh because occlusion has to
    // keep working while the skin is off, and the two want opposite depth and
    // colour-write settings.
    this.mesh = new THREE.Mesh(occluder.sharedGeometry, this.solid)
    this.mesh.frustumCulled = false
    this.mesh.visible = false
    scene.add(this.mesh)
  }

  setMode(mode: SkinMode): void {
    this.mode = mode
    this.mesh.visible = mode !== 'off'
    this.mesh.material = mode === 'wire' ? this.wire : this.solid
  }

  /**
   * Repaints the vertex colours. `floor` is the plane the demo is standing
   * boxes on; when it is null a local fit under `fallbackX`/`fallbackZ` is used
   * so the raw mode still shows something meaningful.
   */
  paint(
    occluder: Occluder,
    floor: GroundPlane | null,
    climbMetres: number,
    fallbackX: number,
    fallbackZ: number
  ): void {
    if (this.mode === 'off') return

    const reference = floor ?? this.fitFallback(occluder, fallbackX, fallbackZ)
    if (!reference) return

    const { colours, positions, metres, cells } = occluder
    const { height, slopeX, slopeZ, originX, originZ } = reference

    for (let cell = 0; cell < cells; cell += 1) {
      const offset = cell * 3
      if (metres[cell] === 0) {
        colours[offset] = 0
        colours[offset + 1] = 0
        colours[offset + 2] = 0
        continue
      }

      const dx = positions[offset]! - originX
      const dz = positions[offset + 2]! - originZ
      const above = positions[offset + 1]! - (height + slopeX * dx + slopeZ * dz)

      if (above >= climbMetres) {
        const t = Math.min(1, (above - climbMetres) / WALL_SPAN)
        colours[offset] = WALL_LOW[0]! + (WALL_HIGH[0]! - WALL_LOW[0]!) * t
        colours[offset + 1] = WALL_LOW[1]! + (WALL_HIGH[1]! - WALL_LOW[1]!) * t
        colours[offset + 2] = WALL_LOW[2]! + (WALL_HIGH[2]! - WALL_LOW[2]!) * t
        continue
      }

      const colour =
        above < BELOW_METRES ? BELOW : above < GROUND_BAND_METRES ? GROUND : IGNORED
      colours[offset] = colour[0]!
      colours[offset + 1] = colour[1]!
      colours[offset + 2] = colour[2]!
    }

    occluder.markColoursDirty()
  }

  dispose(): void {
    this.mesh.removeFromParent()
    this.solid.dispose()
    this.wire.dispose()
  }

  private fitFallback(occluder: Occluder, x: number, z: number): GroundPlane | null {
    const plane = fitSupport(
      occluder.positions,
      occluder.metres,
      occluder.cells,
      x,
      z,
      REFERENCE_RADIUS
    )
    if (!plane) return null

    return {
      height: plane.height,
      slopeX: plane.slopeX,
      slopeZ: plane.slopeZ,
      originX: x,
      originZ: z,
      rms: plane.rms,
      inliers: plane.samples,
    }
  }
}
